import {
  ApprovalLetterInfo,
  defaultValues,
  DeliveryConfirmationMode,
  GeneralContext,
} from "@/components/context/GeneralContext"
import ModalDarkProvider from "@/components/context/ModalDarkProvider"
import { ValidationResponse } from "@/components/google-map/helpers/validateProjectSize"
import { WebsiteLoader } from "@/components/layout/WebsiteLoader"
import Drawer from "@/components/modal/Drawer"
import { Modal } from "@/components/modal/Modal"
import UserGuide from "@/components/user-guide/UserGuide"
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics"
import { usePageMeta } from "@/hooks/usePageMeta"
import { UploadDataItem } from "@/pages/application/content/Documents"
import { CreateAoiData, CreateAppData, CreatedFeatures } from "@/types"
import { Layout } from "antd"
import { FeatureCollection } from "geojson"
import { FC, lazy, Suspense, useRef, useState } from "react"
import { Outlet } from "react-router"
import "./index.less"

// All ~90 platform modals, and the drawers, each in a chunk loaded the first time one opens. Statically imported, they
// put pdf.js, Mapbox and the map tools into the first download of every page, the public website included.
const ModalContent = lazy(() => import("@/components/modal/ModalContent"))
const DrawerContent = lazy(() => import("@/components/modal/DrawerContent"))

const PlatformLayout: FC = () => {
  // Initialize and track route changes with Google Analytics
  // The hook has guards to prevent multiple initializations
  useGoogleAnalytics()
  usePageMeta()

  const [isModalOpen, setIsModalOpen] = useState(defaultValues.isModalOpen)
  // Load the modal contents with the first modal, then keep them mounted as before (close animations, kept state).
  const modalOpenedOnce = useRef(false)
  if (isModalOpen) modalOpenedOnce.current = true
  const [isDrawerOpen, setIsDrawerOpen] = useState(defaultValues.isDrawerOpen)
  const drawerOpenedOnce = useRef(false)
  if (isDrawerOpen) drawerOpenedOnce.current = true
  const [createAppCurrentStep, setCreateAppCurrentStep] = useState(defaultValues.createAppCurrentStep)
  const [modalType, setModalType] = useState(defaultValues.modalType)
  const [drawerType, setDrawerType] = useState(defaultValues.drawerType)
  const [currentApplicationId, setCurrentApplicationId] = useState(defaultValues.currentApplicationId)
  const [currentCompanyId, setCurrentCompanyId] = useState(defaultValues.currentCompanyId)
  const [currentId, setCurrentId] = useState(defaultValues.currentId)
  const [currentApprovalItemId, setCurrentApprovalItemId] = useState(defaultValues.currentApprovalItemId)
  const [locationConfirmationFormStep, setLocationConfirmationFormStep] = useState(
    defaultValues.locationConfirmationFormStep
  )
  const [triggerRefetch, setTriggerRefetch] = useState<boolean | null>(null)
  const [modalSettings, setModalSettings] = useState(defaultValues.modalSettings)
  const [documentInfo, setDocumentInfo] = useState(defaultValues.documentInfo)
  const [sidebarMode, setSidebarMode] = useState(defaultValues.sidebarMode)
  const [isPaymentCreated, setIsPaymentCreated] = useState(defaultValues.isPaymentCreated)
  const [isReadyForReview, setIsReadyForReview] = useState(defaultValues.isReadyForReview)
  const [userLevelOfApproval, setUserLevelOfApproval] = useState(defaultValues.userLevelOfApproval)
  const [modalWidth, setModalWidth] = useState(defaultValues.modalWidth)
  const [modalMode, setModalMode] = useState(defaultValues.modalMode)
  const [isFullScreen, setIsFullScreen] = useState(defaultValues.isFullScreen)
  const [drawerWidth, setDrawerWidth] = useState(defaultValues.modalWidth)
  const [modalTopOffset, setModalTopOffset] = useState(defaultValues.modalTopOffset)
  const [mode, setMode] = useState(defaultValues.mode)
  const [pageViewMode, setPageViewMode] = useState(defaultValues.pageViewMode)
  const [tabActiveKey, setTabActiveKey] = useState(defaultValues.tabActiveKey)
  const [approvalCategory, setApprovalCategory] = useState(defaultValues.approvalCategory)
  const [isLoading, setIsLoading] = useState(defaultValues.isLoading)
  const [closedInfoPopupAppIds, setClosedInfoPopupAppIds] = useState(defaultValues.closedInfoPopupAppIds)
  const [url, setUrl] = useState(defaultValues.url)
  const [imageId, setImageId] = useState(defaultValues.imageId)
  const [approvalLetterInfo, setApprovalLetterInfo] = useState<ApprovalLetterInfo | null>(
    defaultValues.approvalLetterInfo
  )
  const [expiryDate, setExpiryDate] = useState(defaultValues.expiryDate)
  const [assignModalMode, setAssignModalMode] = useState(defaultValues.assignModalMode)
  const [isApprovalMode, setIsApprovalMode] = useState(defaultValues.isApprovalMode)
  const [currentApprovalOrgId, setCurrentApprovalOrgId] = useState(defaultValues.currentApprovalOrgId)
  const [createdContactIds, setCreatedContactIds] = useState(defaultValues.createdContactIds)
  const [isAffected, setIsAffected] = useState(defaultValues.isAffected)
  const [isApproved, setIsApproved] = useState(defaultValues.isApproved)
  const [currentLevelOfService, setCurrentLevelOfService] = useState(defaultValues.currentLevelOfService)
  const [currentEmailMessageId, setCurrentEmailMessageId] = useState(defaultValues.currentEmailMessageId)
  const [emailBodyDocument, setEmailBodyDocument] = useState<ApprovalLetterInfo | null>(defaultValues.emailBodyDocument)
  const [totalNewNotifications, setTotalNewNotifications] = useState(defaultValues.totalNewNotifications)
  const [totalAppNewNotifications, setTotalAppNewNotifications] = useState(defaultValues.totalNewNotifications)
  const [projectGeometryInfo, setProjectGeometryInfo] = useState<ValidationResponse | null>(
    defaultValues.projectGeometryInfo
  )
  const [projectFeatures, setProjectFeatures] = useState<CreatedFeatures | null>(defaultValues.projectFeatures)
  const [projectGeoJson, setProjectGeoJson] = useState<FeatureCollection | null>(defaultValues.projectGeoJson)
  const [kmlUploadedId, setKmlUploadedId] = useState<string | null>(defaultValues.kmlUploadedId)
  const [currentOrgSlug, setCurrentOrgSlug] = useState<Maybe<string>>(defaultValues.currentOrgSlug)
  const [isGuideOpened, setIsGuideOpened] = useState(defaultValues.isGuideOpened)
  const [openedGuideTopic, setOpenedGuideTopic] = useState(defaultValues.openedGuideTopic)
  const [applicationSource, setApplicationSource] = useState(defaultValues.applicationSource)
  const [isNewEmails, setIsNewEmails] = useState(defaultValues.isNewEmails)
  const [isGetReportMode, setIsGetReportMode] = useState(defaultValues.isGetReportMode)
  const [currentCompany, setCurrentCompany] = useState(defaultValues.currentCompany)
  const [currentProject, setCurrentProject] = useState(defaultValues.currentProject)
  const [createAppData, setCreateAppData] = useState<Maybe<Partial<CreateAppData>>>(defaultValues.createAppData)
  const [createAoiData, setCreateAoiData] = useState<Maybe<Partial<CreateAoiData>>>(defaultValues.createAoiData)
  const [navigateLink, setNavigateLink] = useState(defaultValues.navigateLink)
  const [siteContacts, setSiteContacts] = useState(defaultValues.siteContacts)
  const [currentApprovalItems, setCurrentApprovalItems] = useState(defaultValues?.currentApprovalItems)
  const [deliveryApplications, setDeliveryApplications] = useState(defaultValues.deliveryApplications)
  const [currentAoi, setCurrentAoi] = useState<Maybe<AoiEntity>>(defaultValues?.currentAoi)
  const [currentReasonForRejection, setCurrentReasonForRejection] = useState(defaultValues?.currentReasonForRejection)

  return (
    <GeneralContext.Provider
      value={{
        currentAoi,
        setCurrentAoi,
        isApproved,
        setIsApproved,
        createAoiData,
        setCreateAoiData,
        currentProject,
        setCurrentProject,
        currentCompany,
        setCurrentCompany,
        isGetReportMode,
        setIsGetReportMode,
        isNewEmails,
        setIsNewEmails,
        applicationSource,
        setApplicationSource,
        openedGuideTopic,
        setOpenedGuideTopic,
        isGuideOpened,
        setIsGuideOpened,
        currentOrgSlug,
        setCurrentOrgSlug,
        kmlUploadedId,
        setKmlUploadedId,
        projectGeoJson,
        setProjectGeoJson,
        projectFeatures,
        setProjectFeatures,
        projectGeometryInfo,
        setProjectGeometryInfo,
        totalAppNewNotifications,
        setTotalAppNewNotifications,
        totalNewNotifications,
        setTotalNewNotifications,
        currentEmailMessageId,
        setCurrentEmailMessageId,
        emailBodyDocument,
        setEmailBodyDocument,
        currentLevelOfService,
        setCurrentLevelOfService,
        isAffected,
        setIsAffected,
        createdContactIds,
        setCreatedContactIds,
        currentApprovalOrgId,
        setCurrentApprovalOrgId,
        isApprovalMode,
        setIsApprovalMode,
        assignModalMode,
        setAssignModalMode,
        expiryDate,
        setExpiryDate,
        approvalLetterInfo,
        setApprovalLetterInfo,
        url,
        setUrl,
        imageId,
        setImageId,
        closedInfoPopupAppIds,
        setClosedInfoPopupAppIds,
        isLoading,
        setIsLoading,
        approvalCategory,
        setApprovalCategory,
        drawerType,
        setDrawerType,
        drawerWidth,
        setDrawerWidth,
        isDrawerOpen,
        setIsDrawerOpen,
        sidebarMode,
        modalType,
        setModalType,
        isModalOpen,
        setIsModalOpen,
        locationConfirmationFormStep,
        setLocationConfirmationFormStep,
        createAppCurrentStep,
        setCreateAppCurrentStep,
        setSidebarMode,
        triggerRefetch,
        setTriggerRefetch,
        modalSettings,
        setModalSettings,
        currentApplicationId,
        setCurrentApplicationId,
        currentCompanyId,
        setCurrentCompanyId,
        documentInfo,
        setDocumentInfo,
        currentId,
        setCurrentId,
        currentApprovalItemId,
        setCurrentApprovalItemId,
        isPaymentCreated,
        setIsPaymentCreated,
        isReadyForReview,
        setIsReadyForReview,
        userLevelOfApproval,
        setUserLevelOfApproval,
        modalWidth,
        setModalWidth,
        modalTopOffset,
        setModalTopOffset,
        mode,
        setMode,
        pageViewMode,
        setPageViewMode,
        tabActiveKey,
        setTabActiveKey,
        createAppData,
        setCreateAppData,
        navigateLink,
        setNavigateLink,
        siteContacts,
        setSiteContacts,
        currentApprovalItems,
        setCurrentApprovalItems,
        deliveryApplications,
        setDeliveryApplications,
        currentReasonForRejection,
        setCurrentReasonForRejection,
        modalMode,
        setModalMode,
        isFullScreen,
        setIsFullScreen,
      }}
    >
      <ModalDarkProvider>
        <Layout>
          <Suspense fallback={<WebsiteLoader />}>
            <Outlet />
          </Suspense>
        </Layout>
        <Modal
          open={isModalOpen}
          width={modalWidth ? modalWidth : 450}
          topOffset={modalTopOffset}
          mode={modalMode}
          isFullScreen={isFullScreen}
        >
          {modalOpenedOnce.current && (
            <Suspense fallback={null}>
              <ModalContent />
            </Suspense>
          )}
        </Modal>
        <Drawer open={isDrawerOpen} width={drawerWidth ? drawerWidth : 650}>
          {drawerOpenedOnce.current && (
            <Suspense fallback={null}>
              <DrawerContent />
            </Suspense>
          )}
        </Drawer>
        {isGuideOpened && <UserGuide />}
      </ModalDarkProvider>
    </GeneralContext.Provider>
  )
}
export { PlatformLayout }
